import { Assignment, IAssignment } from '@app/models/Assignment'
import { IAttachment } from '@app/models/Attachment'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export enum AssignmentDetailScope {
  Activity = 'Activity',
  Details = 'Details',
  Files = 'Files',
}

export interface IAssignmentDetailState {
  readonly error: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly item: IAssignment
  readonly permissions: IEntityPermissions
  readonly scope: AssignmentDetailScope
  readonly showConfirmDelete: boolean
}

const AssignmentDetailRecord = Record({
  error: '',
  isDeleting: false,
  isLoading: false,
  item: new Assignment(),
  permissions: new EntityPermissions(),
  scope: AssignmentDetailScope.Activity,
  showConfirmDelete: false,
})

export class AssignmentDetailState extends AssignmentDetailRecord
  implements IAssignmentDetailState {
  public static fromJS(
    initialAssignmentDetail?: Partial<IAssignmentDetailState>,
  ) {
    return new AssignmentDetailState(initialAssignmentDetail)
  }

  public readonly error: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly item: Assignment
  public readonly permissions: EntityPermissions
  public readonly scope: AssignmentDetailScope
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<IAssignmentDetailState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteAssignment = 'ASSIGNMENT_DETAIL::DELETE',
  DeleteAssignmentFailure = 'ASSIGNMENT_DETAIL::DELETE_FAILURE',
  DeleteAssignmentRequest = 'ASSIGNMENT_DETAIL::DELETE_REQUEST',
  DeleteAssignmentSuccess = 'ASSIGNMENT_DETAIL::DELETE_SUCCESS',
  LoadAssignment = 'ASSIGNMENT_DETAIL::LOAD_ASSIGNMENT',
  LoadAssignmentRequest = 'ASSIGNMENT_DETAIL::LOAD_ASSIGNMENT_REQUEST',
  LoadAssignmentSuccess = 'ASSIGNMENT_DETAIL::LOAD_ASSIGNMENT_SUCCESS',
  LoadAssignmentFailure = 'ASSIGNMENT_DETAIL::LOAD_ASSIGNMENT_FAILURE',
  ResetAssignmentDetail = 'ASSIGNMENT_DETAIL::RESET_STATE',
  SetAttachments = 'ASSIGNMENT_DETAIL::SET_ATTACHMENTS',
  SetRelatedAttachments = 'ASSIGNMENT_DETAIL::SET_RELATED_ATTACHMENTS',
  SetScope = 'ASSIGNMENT_DETAIL::SET_SCOPE',
  ToggleConfirmDelete = 'ASSIGNMENT_DETAIL::TOGGLE_CONFIRM_DELETE',
}

interface IDeleteAssignmentAction {
  assignmentId: string
  type: ActionTypes.DeleteAssignment
}

interface IDeleteAssignmentFailureAction {
  error: string
  type: ActionTypes.DeleteAssignmentFailure
}

interface IDeleteAssignmentRequestAction {
  type: ActionTypes.DeleteAssignmentRequest
}

interface IDeleteAssignmentSuccessAction {
  type: ActionTypes.DeleteAssignmentSuccess
}

interface ILoadAssignmentFailureAction {
  error: string
  type: ActionTypes.LoadAssignmentFailure
}

interface ILoadAssignmentRequestAction {
  type: ActionTypes.LoadAssignmentRequest
}

interface ILoadAssignmentSuccessAction {
  item: Assignment
  permissions: EntityPermissions
  type: ActionTypes.LoadAssignmentSuccess
}

interface ILoadAssignmentDetailAction {
  type: ActionTypes.LoadAssignment
  value: string
}

interface IResetAssignmentDetailAction {
  type: ActionTypes.ResetAssignmentDetail
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface ISetRelatedAttachmentsAction {
  id: string
  type: ActionTypes.SetRelatedAttachments
  value: List<IAttachment>
}

interface ISetScopeAction {
  scope: AssignmentDetailScope
  type: ActionTypes.SetScope
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IDeleteAssignmentFailureAction
  | IDeleteAssignmentRequestAction
  | IDeleteAssignmentSuccessAction
  | ILoadAssignmentFailureAction
  | ILoadAssignmentRequestAction
  | ILoadAssignmentSuccessAction
  | IResetAssignmentDetailAction
  | ISetAttachmentsAction
  | ISetRelatedAttachmentsAction
  | ISetScopeAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteAssignment(assignmentId: string): IDeleteAssignmentAction {
  return {
    assignmentId,
    type: ActionTypes.DeleteAssignment,
  }
}

function deleteAssignmentFailure(
  error: string,
): IDeleteAssignmentFailureAction {
  return {
    error,
    type: ActionTypes.DeleteAssignmentFailure,
  }
}

function deleteAssignmentRequest(): IDeleteAssignmentRequestAction {
  return {
    type: ActionTypes.DeleteAssignmentRequest,
  }
}

function deleteAssignmentSuccess(): IDeleteAssignmentSuccessAction {
  return {
    type: ActionTypes.DeleteAssignmentSuccess,
  }
}

function loadAssignment(value: string): ILoadAssignmentDetailAction {
  return { type: ActionTypes.LoadAssignment, value }
}

function loadAssignmentFailure(error: string): ILoadAssignmentFailureAction {
  return { error, type: ActionTypes.LoadAssignmentFailure }
}

function loadAssignmentRequest(): ILoadAssignmentRequestAction {
  return { type: ActionTypes.LoadAssignmentRequest }
}

function loadAssignmentSuccess(
  item: Assignment,
  permissions: EntityPermissions,
): ILoadAssignmentSuccessAction {
  return {
    item,
    permissions,
    type: ActionTypes.LoadAssignmentSuccess,
  }
}

function resetAssignmentDetail(): IResetAssignmentDetailAction {
  return { type: ActionTypes.ResetAssignmentDetail }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function setRelatedAttachments(
  value: List<IAttachment>,
  id: string,
): ISetRelatedAttachmentsAction {
  return {
    id,
    type: ActionTypes.SetRelatedAttachments,
    value,
  }
}

function setScope(scope: AssignmentDetailScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  deleteAssignment,
  loadAssignment,
  resetAssignmentDetail,
  setAttachments,
  setRelatedAttachments,
  setScope,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<AssignmentDetailState> = (
  state: AssignmentDetailState = new AssignmentDetailState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.DeleteAssignmentFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as AssignmentDetailState

    case ActionTypes.DeleteAssignmentRequest:
      return state.set('isDeleting', true) as AssignmentDetailState

    case ActionTypes.DeleteAssignmentSuccess:
      return state
        .set('isDeleting', false)
        .set('item', new Assignment()) as AssignmentDetailState

    case ActionTypes.LoadAssignmentFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as AssignmentDetailState

    case ActionTypes.LoadAssignmentRequest:
      return state.set('isLoading', true) as AssignmentDetailState

    case ActionTypes.LoadAssignmentSuccess:
      return state
        .set('isLoading', false)
        .set('item', action.item)
        .set('permissions', action.permissions) as AssignmentDetailState

    case ActionTypes.ResetAssignmentDetail:
      return state.set('isLoading', false) as AssignmentDetailState

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['item', 'attachments'],
        action.value,
      ) as AssignmentDetailState

    case ActionTypes.SetRelatedAttachments:
      const raIndex = state.item.relatedAttachments.findIndex((item: any) => {
        return item.referenceId === action.id
      })
      return state.setIn(
        ['item', 'relatedAttachments', raIndex, 'attachments'],
        action.value,
      ) as AssignmentDetailState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as AssignmentDetailState

    case ActionTypes.ToggleConfirmDelete:
      return state.set(
        'showConfirmDelete',
        action.show,
      ) as AssignmentDetailState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteAssignmentSaga(action: IDeleteAssignmentAction) {
  try {
    yield put(deleteAssignmentRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Assignments/${action.assignmentId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/assignments'))
      yield put(deleteAssignmentSuccess())
    } else {
      yield put(deleteAssignmentFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteAssignmentFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadAssignmentSaga(action: ILoadAssignmentDetailAction) {
  try {
    yield put(loadAssignmentRequest())

    const baseUrl = `/api/Assignments/${action.value}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as Assignment

      yield put(
        loadAssignmentSuccess(
          Assignment.fromJS(entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadAssignmentFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteAssignment() {
  yield takeLatest(ActionTypes.DeleteAssignment, deleteAssignmentSaga)
}

function* watchLoadAssignment() {
  yield takeLatest(ActionTypes.LoadAssignment, loadAssignmentSaga)
}

export function* assignmentDetailRootSaga() {
  yield fork(watchDeleteAssignment)
  yield fork(watchLoadAssignment)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): AssignmentDetailState => state.assignmentDetail

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItem = () =>
  createSelector(selectModuleState(), (mState) => mState.item)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
